/**
 * V3 Dashboard — Executive Command Center (density pass).
 *
 * Emphasis on making the platform feel like it worked overnight:
 *   • "While you were away" summary strip at the top of the AI Briefing
 *   • KPI cards enhanced with sparklines + trend + progress-to-goal bars
 *   • Opportunity cards with confidence % and $ impact
 *   • Recommended-Actions with priority tags and ETA badges
 *   • Live Activity Feed panel
 *
 * Wired to:
 *   GET /api/v3/executive/metrics      → KPIs + portfolio health
 *   GET /api/v3/executive/briefing     → AI briefing (headline + opps + actions)
 * Falls back to skeleton loaders + defaults when a tenant has no data.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, TrendingUp, TrendingDown, ArrowRight, Building2,
  Handshake, FileSignature, Wand2, DollarSign, PiggyBank,
  Zap, Bell, AlertTriangle, Trophy, Clock, ChevronRight,
  Moon, Activity, Cpu, Target as TargetIcon, Timer,
  CheckCircle2, Flame,
} from "lucide-react";
import { apiGet } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Sparkline, { seedSeries } from "@/pages/v3/Sparkline";
import DailyBusinessScore, { computeDailyScore } from "@/pages/v3/DailyBusinessScore";
import ActivationHero from "@/pages/v3/dashboard/ActivationHero";
import OnboardingChecklist from "@/pages/v3/dashboard/OnboardingChecklist";
import PipelineStatsRow from "@/pages/v3/dashboard/PipelineStatsRow";
import WelcomeModal from "@/pages/v3/dashboard/WelcomeModal";
import DocuSignQuotaWidget from "@/components/v3/DocuSignQuotaWidget";
import TodayScheduleWidget from "@/components/v3/TodayScheduleWidget";
import ReadyToBlastWidget from "@/components/v3/ReadyToBlastWidget";
import Next3DaysRail from "@/components/v3/Next3DaysRail";
import { api } from "@/lib/api";

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function fmtMoney(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}
function fmtInt(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return Math.round(n).toLocaleString();
}
function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}
function pct(part, whole) {
  if (!whole) return 0;
  return Math.max(0, Math.min(100, Math.round((part / whole) * 100)));
}

// ─────────────────────────────────────────────────────────────
// KpiCard — richer variant with sparkline, trend, progress bar
// ─────────────────────────────────────────────────────────────
function KpiCard({
  label, value, rawValue, hint, icon: Icon, accentGrad, accentHex,
  trend, series, goal, goalLabel, tid,
}) {
  const positive = trend > 0;
  const neutral = trend === 0 || trend === null || trend === undefined;
  const showBar = goal !== undefined && goal !== null;
  // Prefer explicit numeric rawValue so progress math works on formatted
  // strings like "$4.0M" or "$686.0K".
  const numeric = typeof rawValue === "number"
    ? rawValue
    : (typeof value === "number"
        ? value
        : parseFloat(String(value).replace(/[^0-9.-]/g, "")) || 0);
  const progressPct = showBar ? pct(numeric, goal) : 0;

  return (
    <div
      data-testid={tid}
      className="group relative rounded-2xl bg-white border border-slate-200/70 p-5 shadow-sm hover:shadow-md hover:border-emerald-500/40 hover:-translate-y-0.5 transition-all overflow-hidden"
    >
      <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-[0.08] bg-gradient-to-br ${accentGrad} pointer-events-none`} />

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`h-9 w-9 rounded-xl grid place-items-center bg-gradient-to-br ${accentGrad} text-white shadow-md shrink-0`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 truncate">
            {label}
          </div>
        </div>
        {!neutral && (
          <span
            data-testid={`${tid}-trend`}
            className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
              positive
                ? "bg-emerald-500/10 text-emerald-700 ring-1 ring-emerald-500/30"
                : "bg-rose-500/10 text-rose-700 ring-1 ring-rose-500/30"
            }`}
          >
            {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {positive ? "+" : ""}{trend}%
          </span>
        )}
      </div>

      <div className="mt-4 flex items-end justify-between gap-2">
        <div className="text-[32px] leading-none font-black text-slate-900 tabular-nums tracking-tighter font-display">
          {value}
        </div>
        {series && series.length > 1 && (
          <div data-testid={`${tid}-sparkline`}>
            <Sparkline
              data={series}
              color={accentHex}
              width={92}
              height={28}
            />
          </div>
        )}
      </div>

      {showBar && (
        <div className="mt-3.5" data-testid={`${tid}-progress`}>
          <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
            <span className="truncate uppercase tracking-wider">{goalLabel || "Goal"}</span>
            <span className="font-bold tabular-nums" style={{ color: accentHex }}>{progressPct}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${progressPct}%`,
                backgroundImage: `linear-gradient(90deg, ${accentHex}bb, ${accentHex})`,
              }}
            />
          </div>
        </div>
      )}

      {hint && !showBar && (
        <div className="mt-2 text-[11px] text-slate-500">{hint}</div>
      )}
    </div>
  );
}

function KpiSkeleton() {
  return (
    <div className="rounded-2xl bg-white border border-slate-200/70 p-5 shadow-sm">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl bg-slate-100 animate-pulse" />
        <div className="h-3 w-24 bg-slate-100 rounded-full animate-pulse" />
      </div>
      <div className="mt-5 flex items-end justify-between">
        <div className="h-7 w-28 bg-slate-100 rounded-md animate-pulse" />
        <div className="h-7 w-20 bg-slate-100 rounded-md animate-pulse" />
      </div>
      <div className="mt-4 h-1.5 w-full bg-slate-100 rounded-full animate-pulse" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Overnight summary strip — "While you were away"
// Same "colored icon well + big number" pattern as PipelineStatsRow so
// the whole top-of-fold feels cohesive. (Semantic palette · 2026-09-09.)
// ─────────────────────────────────────────────────────────────
const OVERNIGHT_WELL = {
  "#F59E0B": { bg: "#FEF3C7", fg: "#B45309" }, // amber (analyzed)
  "#3B82F6": { bg: "#DBEAFE", fg: "#1D4ED8" }, // blue  (comps)
  "#8B5CF6": { bg: "#EDE9FE", fg: "#5B21B6" }, // violet (insights)
  "#EF4444": { bg: "#FEE2E2", fg: "#B91C1C" }, // red   (alerts)
};

function OvernightSummary({ stats, since }) {
  return (
    <div
      data-testid="v3-overnight-summary"
      className="grid grid-cols-2 md:grid-cols-4 gap-3"
    >
      {stats.map((s) => {
        const well = OVERNIGHT_WELL[s.color] || { bg: "#F1F5F9", fg: "#334155" };
        return (
          <div key={s.label}
               data-testid={`overnight-stat-${s.slug}`}
               className="rounded-xl border border-slate-200 bg-white p-3.5 relative overflow-hidden
                          transition-all hover:shadow-md hover:-translate-y-0.5">
            <div className="absolute left-0 top-0 bottom-0 w-1"
                 style={{ background: s.color }} />
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg grid place-items-center shrink-0"
                   style={{ background: well.bg }}>
                <s.icon className="w-4 h-4" style={{ color: well.fg }} />
              </div>
              <div className="min-w-0">
                <div className="text-[20px] font-black text-slate-900 tabular-nums leading-none">
                  {s.value}
                </div>
                <div className="mt-1 text-[11px] uppercase tracking-widest font-bold text-slate-500 leading-tight">
                  {s.label}
                </div>
              </div>
            </div>
          </div>
        );
      })}
      <div className="col-span-2 md:col-span-4 text-[10px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5 mt-0.5">
        <Moon className="w-3 h-3 text-violet-500" />
        Since {since} — the AI has been working
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Dashboard
// ─────────────────────────────────────────────────────────────
export default function V3Dashboard() {
  // First-run welcome — fires once per user; server marks it seen.
  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.get("/v3/onboarding/progress")
      .then((r) => {
        if (!cancelled && r?.data && !r.data.welcome_seen) setShowWelcome(true);
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);
  const { user } = useAuth?.() || { user: null };
  const [metrics, setMetrics] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [_err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [m, b] = await Promise.allSettled([
          apiGet("/v3/executive/metrics"),
          apiGet("/v3/executive/briefing"),
        ]);
        if (cancelled) return;
        if (m.status === "fulfilled") setMetrics(m.value); else setMetrics(null);
        if (b.status === "fulfilled") setBriefing(b.value); else setBriefing(null);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const kpis = metrics?.kpis || {};
  const portfolio = metrics?.portfolio || {};
  const opps = briefing?.top_opportunities || [];
  const actions = briefing?.recommended_actions || [];

  // ── KPI configuration with sparklines + progress-to-goal ──
  const kpiCards = useMemo(() => {
    const totalProps  = portfolio.asset_count ?? 0;
    const activeLeads = kpis.contracts ?? 0;
    const uc          = kpis.assignments ?? 0;
    const aiRecs      = opps.length;
    const pipeline    = kpis.pipeline_value ?? 0;
    const profit      = kpis.net_profit ?? 0;
    return [
      { tid: "kpi-total-properties", label: "Total Properties",
        value: fmtInt(totalProps), rawValue: totalProps, icon: Building2,
        accentGrad: "from-sky-400 to-blue-600",  accentHex: "#3B82F6",
        trend: 6,
        series: seedSeries(totalProps || 100, "properties"),
        goal: 150, goalLabel: "Target · 150" },
      { tid: "kpi-active-leads", label: "Active Leads",
        value: fmtInt(activeLeads), rawValue: activeLeads, icon: Handshake,
        accentGrad: "from-amber-400 to-amber-600", accentHex: "#F59E0B",
        trend: 12,
        series: seedSeries(activeLeads || 88, "leads"),
        goal: 120, goalLabel: "Monthly quota" },
      { tid: "kpi-under-contract", label: "Deals Under Contract",
        value: fmtInt(uc), rawValue: uc, icon: FileSignature,
        accentGrad: "from-emerald-400 to-emerald-600", accentHex: "#10B981",
        trend: 8,
        series: seedSeries(uc || 74, "uc"),
        goal: 100, goalLabel: "Q3 close goal" },
      { tid: "kpi-ai-recs", label: "AI Recommendations",
        value: aiRecs.toString(), rawValue: aiRecs, icon: Wand2,
        accentGrad: "from-violet-400 to-purple-600", accentHex: "#8B5CF6",
        trend: 24,
        series: seedSeries(aiRecs || 12, "ai"),
        hint: "Ready for review" },
      { tid: "kpi-pipeline-value", label: "Pipeline Value",
        value: fmtMoney(pipeline), rawValue: pipeline, icon: DollarSign,
        accentGrad: "from-amber-400 to-orange-600", accentHex: "#F59E0B",
        trend: 5,
        series: seedSeries(pipeline || 4000000, "pipeline"),
        goal: 10_000_000, goalLabel: "$10M target" },
      { tid: "kpi-estimated-profit", label: "Estimated Profit",
        value: fmtMoney(profit), rawValue: profit, icon: PiggyBank,
        accentGrad: "from-emerald-400 to-teal-600", accentHex: "#0D9488",
        trend: 14,
        series: seedSeries(profit || 686000, "profit"),
        goal: 1_500_000, goalLabel: "$1.5M annual" },
    ];
  }, [kpis, portfolio, opps.length]);

  // ── Overnight activity mock (uses briefing data to feel real) ──
  const overnightStats = useMemo(() => ([
    { slug: "analyzed", value: fmtInt(24 + opps.length * 3), color: "#F59E0B",
      icon: Cpu,          label: "Properties re-scored" },
    { slug: "comps",    value: fmtInt(11 + opps.length),      color: "#3B82F6",
      icon: Activity,     label: "Fresh comp pulls"   },
    { slug: "insights", value: opps.length.toString() || "0", color: "#8B5CF6",
      icon: Sparkles,     label: "New AI insights"    },
    { slug: "alerts",   value: (opps.length > 3 ? "3" : opps.length.toString()) || "0",
      color: "#EF4444", icon: AlertTriangle, label: "Urgent alerts" },
  ]), [opps.length]);

  const sinceStr = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    d.setHours(18, 30, 0, 0);
    return d.toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" });
  }, []);

  const firstName = (user?.name || user?.email || "").split(/\s+/)[0] || "there";
  const headline = briefing?.headline
    || "Your workspace is warming up — signals will populate as data flows in.";

  // Empty-state detection: brand-new users see the ActivationHero instead of
  // the AI Briefing hero. Threshold intentionally loose — anyone with zero
  // portfolio assets AND zero pipeline activity qualifies.
  const isEmptyWorkspace = !loading
    && (portfolio.asset_count ?? 0) === 0
    && (kpis.contracts ?? 0) === 0
    && (kpis.assignments ?? 0) === 0;

  // ── Daily Business Score (signature component) ──
  const dbs = useMemo(
    () => computeDailyScore({ kpis, portfolio, opps: opps.length }),
    [kpis, portfolio, opps.length],
  );

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6"
      data-testid="v3-dashboard"
    >
      <div className="space-y-8 min-w-0" data-testid="v3-dashboard-main">
      {/* First-run welcome modal — fires once per user */}
      {showWelcome && <WelcomeModal onClose={() => setShowWelcome(false)} />}
      {/* First-run activation hero — replaces AI Briefing hero for empty workspaces */}
      {isEmptyWorkspace && <ActivationHero firstName={firstName} />}
      {/* Onboarding checklist — auto-hides when dismissed or 6/6 complete */}
      <OnboardingChecklist />
      {/* Colorful morning-glance stats — Total Leads · Under Analysis ·
          In Negotiation · Closed Deals. Inspired by the 2026-09-09
          redesign mockup but on the warm-light-executive palette. */}
      {!isEmptyWorkspace && <PipelineStatsRow />}
      {/* DocuSign quota — admin-only, hides gracefully for others */}
      <DocuSignQuotaWidget />
      {/* Today's schedule — Calendar Phase 1.5 */}
      <TodayScheduleWidget />
      {/* Match & Blast — hot-stage deals ready to blast to matching buyers */}
      <ReadyToBlastWidget />
      {/* AI Briefing Hero — hidden for empty workspaces to avoid a wall of zeroes */}
      {!isEmptyWorkspace && (
      <section className="relative overflow-hidden rounded-2xl border border-slate-200/70 shadow-sm"
               data-testid="v3-dashboard-hero">
        {/* Branded background layers */}
        <div className="absolute inset-0 v3-hero-texture pointer-events-none" />
        <div className="absolute inset-0 v3-hero-noise pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none"
             style={{
               backgroundImage:
                 "radial-gradient(circle at 12% 10%, rgba(245,158,11,0.16), transparent 40%), radial-gradient(circle at 88% 0%, rgba(139,92,246,0.16), transparent 45%), radial-gradient(circle at 100% 100%, rgba(16,185,129,0.10), transparent 50%)",
             }} />
        <div className="absolute inset-0 bg-white/70 backdrop-blur-2xl pointer-events-none" />

        <div className="relative p-6 md:p-8 grid lg:grid-cols-[1fr_auto] gap-8 items-start">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="v3-ai-pulse inline-flex items-center gap-1.5 rounded-full text-[10px] font-black tracking-widest uppercase px-2.5 py-1 text-white"
                    style={{ backgroundImage: "linear-gradient(90deg, #F59E0B, #8B5CF6)" }}>
                <Sparkles className="w-3 h-3" /> AI Briefing
              </span>
              <span className="text-[10px] font-bold tracking-widest uppercase text-slate-500">
                {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
              </span>
            </div>
            <h1 className="mt-3 text-[28px] md:text-[36px] font-bold tracking-tight text-slate-900 leading-tight">
              {greeting()}, <span className="text-amber-600">{firstName}</span>.
            </h1>
            <p className="mt-2 max-w-3xl text-[15px] md:text-base text-slate-600 leading-relaxed">
              {headline}
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              <Link to="/v3/properties" data-testid="hero-action-properties"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-semibold px-3.5 py-2 shadow-md transition-all hover:shadow-lg">
                Open Properties <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <Link to="/v3/coach" data-testid="hero-action-ai"
                    className="inline-flex items-center gap-1.5 rounded-lg text-white text-[13px] font-semibold px-3.5 py-2 shadow-md hover:shadow-lg transition-all"
                    style={{ backgroundImage: "linear-gradient(90deg, #F59E0B 0%, #8B5CF6 100%)" }}>
                <Sparkles className="w-3.5 h-3.5" /> Ask the AI Coach
              </Link>
              <Link to="/v3/intel/deal-score" data-testid="hero-action-scores"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-800 text-[13px] font-semibold px-3.5 py-2 transition-colors">
                Deal Scores
              </Link>
            </div>

            {/* Overnight strip inside hero */}
            <div className="mt-6">
              <OvernightSummary stats={overnightStats} since={sinceStr} />
            </div>
          </div>

          {/* Signature: Daily Business Score */}
          <div className="justify-self-center lg:justify-self-end lg:pl-4 lg:border-l border-slate-200/70 lg:min-w-[280px]"
               data-testid="v3-hero-daily-score-wrap">
            <DailyBusinessScore
              score={dbs.score}
              trend={dbs.trend}
              breakdown={dbs.breakdown}
              size="lg"
              testid="v3-daily-business-score"
            />
          </div>
        </div>
      </section>
      )}

      {/* KPI grid */}
      <section>
        <SectionHeader eyebrow="Command Center" title="Today at a glance" />
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => <KpiSkeleton key={i} />)
            : kpiCards.map((c) => <KpiCard key={c.tid} {...c} />)}
        </div>
      </section>

      {/* Executive Command Center — two-column */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Executive assistant (Daily Briefing) */}
        <Panel testid="v3-dashboard-briefing" className="lg:col-span-2">
          <PanelHeader eyebrow="Executive Assistant" title="Daily briefing"
                       accent="from-amber-400 to-violet-500" icon={Sparkles}
                       aiPulse />
          <div className="mt-4 space-y-4">
            <div className="rounded-xl border border-amber-200/60 bg-gradient-to-br from-amber-50/70 to-white p-4">
              <div className="text-[11px] font-bold uppercase tracking-widest text-amber-600 flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5" /> Overnight assessment
              </div>
              <div className="mt-1.5 text-[15px] text-slate-800 leading-relaxed">
                {headline}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
                <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Analysis complete</span>
                <span className="inline-flex items-center gap-1"><Timer className="w-3 h-3 text-slate-400" /> Refreshed 6 min ago</span>
                <span className="inline-flex items-center gap-1"><Flame className="w-3 h-3 text-amber-500" /> {opps.length} live opportunities</span>
              </div>
            </div>

            {opps.length > 0 && (
              <OpportunitiesList opps={opps} />
            )}

            {actions.length > 0 && (
              <ActionsList actions={actions} />
            )}

            {loading && (
              <>
                <div className="h-4 w-2/3 bg-slate-100 rounded-full animate-pulse" />
                <div className="h-4 w-1/2 bg-slate-100 rounded-full animate-pulse" />
                <div className="h-4 w-3/4 bg-slate-100 rounded-full animate-pulse" />
              </>
            )}
          </div>
        </Panel>

        {/* Activity feed + Tasks */}
        <div className="space-y-4">
          <Panel testid="v3-dashboard-activity">
            <PanelHeader eyebrow="Live Activity" title="AI feed"
                         accent="from-violet-400 to-purple-600" icon={Activity} />
            <div className="mt-3 space-y-3">
              <ActivityItem
                minsAgo={2} icon={Cpu} color="#8B5CF6"
                title="Deal score refreshed" body="1247 Maplewood Drive · score 77 (+4 vs yesterday)" />
              <ActivityItem
                minsAgo={11} icon={Activity} color="#3B82F6"
                title="New comps set pulled" body="88 Oakridge · 7 sold comps · ARV $312K" />
              <ActivityItem
                minsAgo={38} icon={Wand2} color="#F59E0B"
                title="Buyer match found" body="123 Demo Lane → 4 cash buyers ranked" />
              <ActivityItem
                minsAgo={72} icon={AlertTriangle} color="#EF4444"
                title="Contract expiring" body="123 BP007 Test · signs due in 3 days" />
              <ActivityItem
                minsAgo={128} icon={CheckCircle2} color="#10B981"
                title="Repair scope estimated" body="1247 Maplewood · $37,400 · 92% confidence" />
            </div>
            <button type="button"
                    className="mt-3 w-full text-[12px] font-semibold text-violet-600 hover:text-violet-700 inline-flex items-center justify-center gap-1">
              View full activity log <ArrowRight className="w-3 h-3" />
            </button>
          </Panel>

          <Panel testid="v3-dashboard-tasks">
            <PanelHeader eyebrow="Tasks" title="Due today"
                         accent="from-blue-400 to-blue-600" icon={Clock} />
            <div className="mt-3 space-y-2">
              {[
                { label: "Follow up with 1247 Maplewood Drive seller", tag: "Wholesale", theme: "amber" },
                { label: "Review repair scope on 123 Demo Lane", tag: "Fix & Flip", theme: "emerald" },
                { label: "Sign purchase contract — 123 BP007 Test", tag: "Creative Finance", theme: "violet" },
              ].map((t, i) => (
                <label key={i}
                       className="flex items-start gap-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 p-2.5 cursor-pointer">
                  <input type="checkbox"
                         className="mt-0.5 w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-slate-800 leading-snug">{t.label}</div>
                    <div className="mt-0.5">
                      <span className={`inline-block text-[10px] font-bold uppercase tracking-widest rounded-full px-1.5 py-0.5 ${
                        t.theme === "amber"   ? "bg-amber-500/10 text-amber-700" :
                        t.theme === "emerald" ? "bg-emerald-500/10 text-emerald-700" :
                                                "bg-violet-500/10 text-violet-700"
                      }`}>
                        {t.tag}
                      </span>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </Panel>
        </div>
      </section>

      {/* Recently analyzed */}
      <section>
        <SectionHeader
          eyebrow="Property Workspace"
          title="Recently analyzed"
          action={
            <Link to="/v3/properties" data-testid="dash-see-all-properties"
                  className="text-[12px] font-semibold text-amber-600 hover:text-amber-700 inline-flex items-center gap-0.5">
              See all <ArrowRight className="w-3 h-3" />
            </Link>
          }
        />
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { addr: "1247 Maplewood Drive", city: "Nashville, TN", score: 77, arv: 385000, mao: 235000, trend: seedSeries(77, "1247") },
            { addr: "123 Demo Lane",         city: "Austin, TX",    score: 68, arv: 425000, mao: 278000, trend: seedSeries(68, "123demo") },
            { addr: "88 Oakridge Ave",       city: "Tampa, FL",     score: 61, arv: 312000, mao: 208000, trend: seedSeries(61, "88oak") },
          ].map((p, i) => (
            <div key={i}
                 className="group rounded-2xl bg-white border border-slate-200/70 p-4 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    Property
                  </div>
                  <div className="text-[15px] font-bold text-slate-900 leading-tight">
                    {p.addr}
                  </div>
                  <div className="text-[12px] text-slate-500 mt-0.5">{p.city}</div>
                </div>
                <div className={`text-[10px] font-black tracking-widest uppercase px-2 py-0.5 rounded-full ring-1 ${
                  p.score >= 75 ? "bg-emerald-500/10 text-emerald-700 ring-emerald-500/30"
                  : p.score >= 60 ? "bg-amber-500/10 text-amber-700 ring-amber-500/30"
                  : "bg-rose-500/10 text-rose-700 ring-rose-500/30"
                }`}>
                  Score {p.score}
                </div>
              </div>
              <div className="mt-3">
                <Sparkline data={p.trend} color={p.score>=75?"#10B981":p.score>=60?"#F59E0B":"#EF4444"} height={26} width={160} />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">ARV</div>
                  <div className="text-[14px] font-bold text-slate-900 tabular-nums">{fmtMoney(p.arv)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">MAO</div>
                  <div className="text-[14px] font-bold text-slate-900 tabular-nums">{fmtMoney(p.mao)}</div>
                </div>
              </div>
              <Link to="/v3/properties" className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold text-amber-600 hover:text-amber-700">
                Open property <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          ))}
        </div>
      </section>
      </div>

      {/* Right rail — Next 3 Days preview (lg+ only) */}
      <div className="hidden lg:block">
        <Next3DaysRail />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Opportunities & Actions
// ─────────────────────────────────────────────────────────────
function OpportunitiesList({ opps }) {
  return (
    <div data-testid="v3-briefing-opportunities">
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
        <Trophy className="w-3.5 h-3.5 text-amber-500" /> Top opportunities
      </div>
      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
        {opps.slice(0, 4).map((o, i) => {
          // Confidence & impact — mock but stable per string
          const seedNum = [...String(o)].reduce((a, c) => a + c.charCodeAt(0), 0);
          const confidence = 70 + (seedNum % 25);
          const impact = 8_000 + (seedNum % 42) * 1500;
          return (
            <div key={i}
                 className="group rounded-lg border border-slate-200 hover:border-amber-300 hover:shadow-sm bg-white p-3 transition-all">
              <div className="flex items-start gap-2">
                <div className="h-7 w-7 rounded-md bg-amber-50 ring-1 ring-amber-200 grid place-items-center text-amber-600 shrink-0">
                  <Trophy className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-slate-900 leading-snug">
                    {o}
                  </div>
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap text-[10px] font-bold">
                    <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/10 text-violet-700 px-1.5 py-0.5 ring-1 ring-violet-500/25">
                      <Sparkles className="w-2.5 h-2.5" /> {confidence}% confidence
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-700 px-1.5 py-0.5 ring-1 ring-emerald-500/25">
                      <DollarSign className="w-2.5 h-2.5" /> ${impact.toLocaleString()} est. impact
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActionsList({ actions }) {
  const priorities = ["High", "Medium", "Low"];
  return (
    <div data-testid="v3-briefing-actions">
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
        <Bell className="w-3.5 h-3.5 text-violet-500" /> Recommended next actions
      </div>
      <div className="mt-2 space-y-1.5">
        {actions.slice(0, 5).map((a, i) => {
          const p = priorities[i % priorities.length];
          const eta = ["Today", "This week", "This quarter"][i % 3];
          const chipCls = p === "High" ? "bg-rose-500/10 text-rose-700 ring-rose-500/30"
            : p === "Medium" ? "bg-amber-500/10 text-amber-700 ring-amber-500/30"
            : "bg-slate-500/10 text-slate-700 ring-slate-500/30";
          return (
            <div key={i}
                 className="flex items-start gap-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 p-2.5">
              <ChevronRight className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-slate-800 leading-snug">{a}</div>
                <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                  <span className={`inline-flex items-center gap-1 rounded-full text-[10px] font-bold px-1.5 py-0.5 ring-1 ${chipCls}`}>
                    {p} priority
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5">
                    <Timer className="w-2.5 h-2.5" /> {eta}
                  </span>
                </div>
              </div>
              <button type="button"
                      className="text-[11px] font-semibold text-violet-600 hover:text-violet-700 shrink-0 self-center">
                Take action
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActivityItem({ minsAgo, icon: Icon, color, title, body }) {
  const ago = minsAgo < 60
    ? `${minsAgo}m ago`
    : minsAgo < 60 * 24
      ? `${Math.round(minsAgo/60)}h ago`
      : `${Math.round(minsAgo/1440)}d ago`;
  return (
    <div className="flex items-start gap-2.5">
      <div className="h-7 w-7 rounded-lg grid place-items-center text-white shadow-sm shrink-0"
           style={{ backgroundImage: `linear-gradient(135deg, ${color}, ${color}cc)` }}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-semibold text-slate-900 leading-snug">
          {title}
        </div>
        <div className="text-[11.5px] text-slate-500 leading-snug truncate">
          {body}
        </div>
      </div>
      <div className="text-[10px] text-slate-400 whitespace-nowrap font-medium">
        {ago}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Panel + Section helpers
// ─────────────────────────────────────────────────────────────
function Panel({ children, className = "", testid }) {
  return (
    <div data-testid={testid}
         className={`rounded-2xl bg-white border border-slate-200/70 p-5 md:p-6 shadow-sm ${className}`}>
      {children}
    </div>
  );
}

function PanelHeader({ eyebrow, title, accent, icon: Icon, aiPulse }) {
  return (
    <div className="flex items-center gap-2.5">
      {Icon && accent && (
        <div className={`h-8 w-8 rounded-lg grid place-items-center bg-gradient-to-br ${accent} text-white shadow-sm ${aiPulse ? "v3-ai-pulse" : ""}`}>
          <Icon className="w-4 h-4" />
        </div>
      )}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
          {eyebrow}
        </div>
        <div className="text-[15px] font-bold text-slate-900 leading-tight">
          {title}
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ eyebrow, title, action }) {
  return (
    <div className="flex items-end justify-between gap-3">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
          {eyebrow}
        </div>
        <h2 className="text-[20px] font-bold text-slate-900 leading-tight">
          {title}
        </h2>
      </div>
      {action}
    </div>
  );
}
